import React, { useEffect, useState, useMemo } from 'react';
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend
} from 'recharts';
import { dashboardApi } from '../services/dashboardApi';

export interface EspWeeklyTrendPoint {
    week: string;
    sends: number;
    delivered: number;
    opens: number;
    clicks: number;
    open_rate: number;
    click_rate: number;
    conversions: number;
    revenue_dkk: number;
}

export interface EspKpiRecord {
    esp_id: string;
    label: string;
    brand: string;
    geo: string;
    provider: string;
    role: 'secondary' | 'incubator';
    sending_domain: string;
    status_badge: 'SCALING' | 'STEADY' | 'GUARDED' | 'THROTTLED' | 'INCUBATOR';
    quota_range: [number, number];
    avg_daily_quota: number;
    max_gmail_per_day: number;
    // Pillar 1: Audience & Funnel Velocity
    ready_count: number;
    active_total: number;
    inactive_count: number;
    esp_live_size: number;
    drift_pct: number;
    tier1_hot_14d: number;
    tier2_warm_30d: number;
    tier3_new_recruits: number;
    recruit_grad_pct_30d: number;
    recruits_7d: number;
    flips_ready_7d: number;
    flips_inactive_7d: number;
    net_ready_delta_7d: number;
    pruned_7d: number;
    recruits_30d: number;
    pruned_30d: number;
    // Pillar 2: Candidate Reservoir & Runway
    reservoir_stage1: number;
    reservoir_stage2: number;
    reservoir_total: number;
    runway_days: number | null;
    // Pillar 3: Send Volume & Engagement
    active_openers_7d: number;
    open_rate_7d: number;
    campaign_open_rate_7d: number;
    open_rate_wow_delta: number;
    sends_7d: number;
    delivered_7d: number;
    click_rate_7d: number;
    sends_30d: number;
    delivered_30d: number;
    open_rate_30d: number;
    click_rate_30d: number;
    aff_clicks_7d: number;
    aff_unique_7d: number;
    aff_clicks_30d: number;
    aff_unique_30d: number;
    // Pillar 4: Monetization & Unit Economics
    conv_7d: number;
    rev_dkk_7d: number;
    conv_30d: number;
    rev_dkk_30d: number;
    ecpm_30d: number;
    epc_30d: number;
    shielded_converters: number;
    // Pillar 5: Deliverability & Health Guardrails
    domain_reputation: string;
    spam_rate_pct_7d: number;
    bounce_rate_30d: number;
    complaint_rate_30d: number;
    unsub_rate_30d: number;
    last_activity_age_hours: number | null;
    // Trend
    weekly_trends: EspWeeklyTrendPoint[];
}

export interface EspKpiPayload {
    generated_at: string;
    summary: {
        total_ready_secondary: number;
        total_active_secondary: number;
        total_daily_quota_min: number;
        total_daily_quota_max: number;
        total_recruits_7d: number;
        total_flips_ready_7d: number;
        total_flips_inactive_7d: number;
        total_pruned_7d: number;
        total_delivered_30d: number;
        total_rev_dkk_30d: number;
        total_conv_30d: number;
        total_rev_dkk_7d: number;
        total_conv_7d: number;
        total_aff_clicks_7d: number;
        total_aff_clicks_30d: number;
        reservoir_by_geo: Record<string, number>;
    };
    esps: EspKpiRecord[];
}

interface EspKpiCommandCenterProps {
    onSelectEspForDeepDive?: (country: string, espId: string) => void;
}

const STATUS_BADGE_STYLES: Record<string, { bg: string; text: string; label: string }> = {
    SCALING: {
        bg: 'bg-emerald-50 border-emerald-200',
        text: 'text-emerald-700',
        label: '🚀 SCALING'
    },
    STEADY: {
        bg: 'bg-blue-50 border-blue-200',
        text: 'text-blue-700',
        label: '✅ STEADY'
    },
    GUARDED: {
        bg: 'bg-amber-50 border-amber-200',
        text: 'text-amber-700',
        label: '⚠️ GUARDED'
    },
    THROTTLED: {
        bg: 'bg-rose-50 border-rose-200',
        text: 'text-rose-700',
        label: '🛑 THROTTLED'
    },
    INCUBATOR: {
        bg: 'bg-purple-50 border-purple-200',
        text: 'text-purple-700',
        label: '🌱 INCUBATOR'
    }
};

const POSTMASTER_BADGE: Record<string, string> = {
    HIGH: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    COMPLIANT: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    MEDIUM: 'bg-amber-100 text-amber-800 border-amber-200',
    LOW: 'bg-rose-100 text-rose-800 border-rose-200',
    BAD: 'bg-red-100 text-red-900 border-red-300',
    NONE: 'bg-gray-100 text-gray-600 border-gray-200',
    UNKNOWN: 'bg-gray-100 text-gray-500 border-gray-200'
};

export const EspKpiCommandCenter: React.FC<EspKpiCommandCenterProps> = ({ onSelectEspForDeepDive }) => {
    const [data, setData] = useState<EspKpiPayload | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [geoFilter, setGeoFilter] = useState<string>('ALL');
    const [roleFilter, setRoleFilter] = useState<'secondary' | 'all' | 'incubator'>('secondary');
    const [selectedEspId, setSelectedEspId] = useState<string>('gr_no');
    const [showFrameworkGuide, setShowFrameworkGuide] = useState<boolean>(false);
    const [chartMode, setChartMode] = useState<'revenue' | 'engagement'>('revenue');

    const loadKpis = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await dashboardApi.getEspKpis();
            if (res && Array.isArray(res.esps)) {
                setData(res);
                if (!selectedEspId && res.esps.length > 0) {
                    setSelectedEspId(res.esps[0].esp_id);
                }
            } else {
                setError('Invalid KPI payload returned from API.');
            }
        } catch (err: any) {
            console.error('Failed to load ESP KPIs:', err);
            setError(err.message || 'Failed to load ESP KPIs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadKpis();
    }, []);

    const filteredEsps = useMemo(() => {
        if (!data?.esps) return [];
        return data.esps.filter((e) => {
            if (geoFilter !== 'ALL' && e.geo !== geoFilter) return false;
            if (roleFilter === 'secondary' && e.role !== 'secondary') return false;
            if (roleFilter === 'incubator' && e.role !== 'incubator') return false;
            return true;
        });
    }, [data, geoFilter, roleFilter]);

    const selectedEsp = useMemo(() => {
        if (!data?.esps) return null;
        return (
            filteredEsps.find((e) => e.esp_id === selectedEspId) ||
            data.esps.find((e) => e.esp_id === selectedEspId) ||
            filteredEsps[0] ||
            null
        );
    }, [data, filteredEsps, selectedEspId]);

    // Weighted portfolio metrics across currently filtered ESPs
    const portfolioStats = useMemo(() => {
        const list = filteredEsps;
        const totalReady = list.reduce((acc, r) => acc + (r.ready_count || 0), 0);
        const totalActive = list.reduce((acc, r) => acc + (r.active_total || 0), 0);
        const minQuota = list.reduce((acc, r) => acc + (r.quota_range?.[0] || 0), 0);
        const maxQuota = list.reduce((acc, r) => acc + (r.quota_range?.[1] || 0), 0);
        const recruits7d = list.reduce((acc, r) => acc + (r.recruits_7d || 0), 0);
        const pruned7d = list.reduce((acc, r) => acc + (r.pruned_7d || 0), 0);
        const netReadyDelta7d = list.reduce((acc, r) => acc + (r.net_ready_delta_7d || 0), 0);
        const delivered7d = list.reduce((acc, r) => acc + (r.delivered_7d || 0), 0);
        const delivered30d = list.reduce((acc, r) => acc + (r.delivered_30d || 0), 0);
        const clicks7d = list.reduce((acc, r) => acc + (r.aff_clicks_7d || 0), 0);
        const clicks30d = list.reduce((acc, r) => acc + (r.aff_clicks_30d || 0), 0);
        const rev7d = list.reduce((acc, r) => acc + (r.rev_dkk_7d || 0), 0);
        const rev30d = list.reduce((acc, r) => acc + (r.rev_dkk_30d || 0), 0);
        const conv30d = list.reduce((acc, r) => acc + (r.conv_30d || 0), 0);
        const activeOpeners7d = list.reduce((acc, r) => acc + (r.active_openers_7d || 0), 0);
        const weightedActiveOpenerRate = totalReady > 0 ? (activeOpeners7d / totalReady) * 100 : 0;
        const ecpm30d = delivered30d > 0 ? (rev30d / delivered30d) * 1000 : 0;
        const epc30d = clicks30d > 0 ? rev30d / clicks30d : 0;

        const maxSpam = list.reduce((max, r) => Math.max(max, r.spam_rate_pct_7d || 0), 0);
        const maxBounce = list.reduce((max, r) => Math.max(max, r.bounce_rate_30d || 0), 0);
        const compliantCount = list.filter((r) =>
            ['HIGH', 'COMPLIANT', 'MEDIUM'].includes(r.domain_reputation)
        ).length;

        return {
            totalReady,
            totalActive,
            minQuota,
            maxQuota,
            recruits7d,
            pruned7d,
            netReadyDelta7d,
            delivered7d,
            delivered30d,
            clicks7d,
            clicks30d,
            rev7d,
            rev30d,
            conv30d,
            weightedActiveOpenerRate,
            ecpm30d,
            epc30d,
            maxSpam,
            maxBounce,
            compliantCount,
            count: list.length
        };
    }, [filteredEsps]);

    if (loading && !data) {
        return (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center shadow-sm">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent mb-3"></div>
                <h3 className="text-base font-semibold text-gray-800">Loading Multi-ESP KPI Command Center...</h3>
                <p className="text-xs text-gray-500 mt-1">
                    Aggregating SendSegment tiers, candidate reservoirs, affiliate clicks, revenue, and Postmaster health across all 11 ESPs.
                </p>
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="bg-rose-50 rounded-xl border border-rose-200 p-8 text-center">
                <h3 className="text-base font-bold text-rose-800">Unable to load ESP KPI Command Center</h3>
                <p className="text-xs text-rose-600 mt-1 mb-4">{error}</p>
                <button
                    onClick={loadKpis}
                    className="px-4 py-2 bg-rose-600 text-white rounded-lg text-xs font-semibold hover:bg-rose-700"
                >
                    Retry Loading
                </button>
            </div>
        );
    }

    const geoRes = data?.summary?.reservoir_by_geo || {};
    const totalProvenReservoir = (geoRes.NO || 0) + (geoRes.SE || 0) + (geoRes.FI || 0);

    return (
        <div className="space-y-6">
            {/* Top Banner & 5-Pillar Guide Toggle */}
            <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-950 rounded-xl p-5 text-white shadow-md">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2.5">
                            <span className="px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider bg-indigo-500/30 border border-indigo-400/40 rounded-full text-indigo-200">
                                5-Pillar Multi-ESP Telemetry
                            </span>
                            {data?.generated_at && (
                                <span className="text-xs text-slate-300">
                                    Snapshot: {new Date(data.generated_at).toLocaleString()}
                                </span>
                            )}
                        </div>
                        <h2 className="text-xl font-bold mt-1.5">
                            🎯 Multi-ESP KPI Command Center (NO / SE / FI)
                        </h2>
                        <p className="text-xs text-slate-300 mt-0.5 max-w-3xl">
                            Unified view of audience scaling velocity, FeedBlitz proven-opener reservoir runway, true 7d opener engagement vs. proxy opens, DKK monetization, and Postmaster guardrails.
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2.5">
                        <button
                            onClick={() => setShowFrameworkGuide(!showFrameworkGuide)}
                            className="px-3.5 py-2 rounded-lg text-xs font-semibold bg-white/10 hover:bg-white/20 text-white border border-white/15 transition-colors"
                        >
                            {showFrameworkGuide ? 'Hide 5-Pillar KPI Guide ▲' : '📘 Why These KPIs Matter ▼'}
                        </button>
                        <button
                            onClick={loadKpis}
                            disabled={loading}
                            className="px-3.5 py-2 rounded-lg text-xs font-semibold bg-indigo-500 hover:bg-indigo-600 text-white shadow-sm transition-colors flex items-center gap-1.5"
                        >
                            {loading ? 'Refreshing...' : '↻ Refresh Live KPIs'}
                        </button>
                    </div>
                </div>

                {/* Collapsible 5-Pillar KPI Relevance Guide */}
                {showFrameworkGuide && (
                    <div className="mt-4 pt-4 border-t border-white/15 grid grid-cols-1 md:grid-cols-5 gap-3 text-xs">
                        <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                            <div className="font-bold text-indigo-300 mb-1">1. Audience &amp; Net Velocity</div>
                            <p className="text-slate-300 leading-relaxed">
                                Tracks <strong>Ready SendSegment</strong> size, 3-Tier composition (Tier 1 Hot 14d, Tier 2 Rotating 15-30d, Tier 3 Prove-It 0-7d), and <strong>7d Net Flow</strong> (Recruits In vs Auto-Pruned Out) so we know whether an ESP is compounding or shrinking.
                            </p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                            <div className="font-bold text-emerald-300 mb-1">2. Reservoir &amp; Runway</div>
                            <p className="text-slate-300 leading-relaxed">
                                Measures unallocated <strong>Stage 1 (&le;14d) + Stage 2 (15-90d)</strong> proven openers in the master GEO table and calculates <strong>Days of Runway</strong> at each ESP&apos;s daily intake quota before Stage-3 fallback is needed.
                            </p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                            <div className="font-bold text-amber-300 mb-1">3. True Engagement vs Proxy</div>
                            <p className="text-slate-300 leading-relaxed">
                                Separates Apple MPP / Gmail image-proxy Campaign Open Rate from <strong>7d Active Opener % of Ready Pool</strong> and <strong>Verified Affiliate Clicks</strong> (`admin.affiliate_clicks`).
                            </p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                            <div className="font-bold text-cyan-300 mb-1">4. Monetization &amp; Economics</div>
                            <p className="text-slate-300 leading-relaxed">
                                Connects each ESP to actual affiliate conversions and revenue in DKK (`admin.api_conversions_v`), normalizing performance via <strong>30d eCPM (DKK / 1k delivered)</strong> and <strong>EPC (DKK / click)</strong>.
                            </p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                            <div className="font-bold text-rose-300 mb-1">5. Deliverability Guardrails</div>
                            <p className="text-slate-300 leading-relaxed">
                                Monitors <strong>Google Postmaster Status</strong> (`HIGH`/`COMPLIANT`/`LOW`), <strong>Spam Complaint %</strong> (must stay &lt;0.30%), Hard Bounce %, and Unsubscribe % to protect IP/domain warmups.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* 5-Pillar Executive Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Pillar 1 Card */}
                <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-indigo-600">
                            Pillar 1 • Ready Audience
                        </span>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded bg-indigo-50 text-indigo-700">
                            {portfolioStats.count} ESPs
                        </span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900 mt-2">
                        {portfolioStats.totalReady.toLocaleString()}
                    </div>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 text-xs">
                        <span className="text-gray-500">
                            Active Pool: <strong className="text-gray-800">{portfolioStats.totalActive.toLocaleString()}</strong>
                        </span>
                        <span
                            className={`font-bold ${
                                portfolioStats.recruits7d >= 0 ? 'text-emerald-600' : 'text-amber-600'
                            }`}
                        >
                            +{portfolioStats.recruits7d.toLocaleString()} (7d in)
                        </span>
                    </div>
                </div>

                {/* Pillar 2 Card */}
                <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-emerald-600">
                            Pillar 2 • Proven Reservoir
                        </span>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">
                            {portfolioStats.minQuota}–{portfolioStats.maxQuota}/day
                        </span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900 mt-2">
                        {totalProvenReservoir.toLocaleString()}
                    </div>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 text-xs text-gray-600">
                        <span>NO: {(geoRes.NO || 0).toLocaleString()}</span>
                        <span>SE: {(geoRes.SE || 0).toLocaleString()}</span>
                        <span>FI: {(geoRes.FI || 0).toLocaleString()}</span>
                    </div>
                </div>

                {/* Pillar 3 Card */}
                <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-amber-600">
                            Pillar 3 • 7d Engagement
                        </span>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-50 text-amber-700">
                            {portfolioStats.weightedActiveOpenerRate.toFixed(1)}% Active OR
                        </span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900 mt-2">
                        {portfolioStats.clicks7d.toLocaleString()}{' '}
                        <span className="text-sm font-medium text-gray-500">clicks (7d)</span>
                    </div>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 text-xs text-gray-500">
                        <span>
                            7d Sent: <strong className="text-gray-800">{portfolioStats.delivered7d.toLocaleString()}</strong>
                        </span>
                        <span>
                            30d Clicks: <strong className="text-gray-800">{portfolioStats.clicks30d.toLocaleString()}</strong>
                        </span>
                    </div>
                </div>

                {/* Pillar 4 Card */}
                <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-blue-600">
                            Pillar 4 • 30d Revenue
                        </span>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded bg-blue-50 text-blue-700">
                            {portfolioStats.conv30d} Conv
                        </span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900 mt-2">
                        {Math.round(portfolioStats.rev30d).toLocaleString()}{' '}
                        <span className="text-sm font-semibold text-gray-500">DKK</span>
                    </div>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 text-xs text-gray-500">
                        <span>
                            eCPM: <strong className="text-gray-800">{portfolioStats.ecpm30d.toFixed(1)} DKK</strong>
                        </span>
                        <span>
                            EPC: <strong className="text-gray-800">{portfolioStats.epc30d.toFixed(2)} DKK</strong>
                        </span>
                    </div>
                </div>

                {/* Pillar 5 Card */}
                <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-rose-600">
                            Pillar 5 • Guardrails
                        </span>
                        <span
                            className={`text-xs font-semibold px-2 py-0.5 rounded ${
                                portfolioStats.maxSpam < 0.3
                                    ? 'bg-emerald-50 text-emerald-700'
                                    : 'bg-rose-50 text-rose-700'
                            }`}
                        >
                            {portfolioStats.maxSpam < 0.3 ? 'PASS (<0.3%)' : 'ALERT'}
                        </span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900 mt-2">
                        {portfolioStats.maxSpam.toFixed(2)}%{' '}
                        <span className="text-sm font-medium text-gray-500">Max Spam</span>
                    </div>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 text-xs text-gray-500">
                        <span>
                            Healthy: <strong className="text-emerald-700">{portfolioStats.compliantCount}/{portfolioStats.count}</strong>
                        </span>
                        <span>
                            Max Bounce: <strong className="text-gray-800">{portfolioStats.maxBounce.toFixed(2)}%</strong>
                        </span>
                    </div>
                </div>
            </div>

            {/* Filter & Role Controls Bar */}
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-bold uppercase text-gray-500 mr-1">Country:</span>
                    {(['ALL', 'NO', 'SE', 'FI'] as const).map((g) => (
                        <button
                            key={g}
                            onClick={() => setGeoFilter(g)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 ${
                                geoFilter === g
                                    ? 'bg-blue-600 text-white shadow-sm'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            {g !== 'ALL' && (
                                <img
                                    src={`https://flagcdn.com/w20/${g.toLowerCase()}.png`}
                                    alt={g}
                                    className="w-4 h-3 rounded-sm object-cover"
                                />
                            )}
                            {g === 'ALL' ? 'All GEOs (NO / SE / FI)' : g}
                        </button>
                    ))}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-bold uppercase text-gray-500 mr-1">System Role:</span>
                    <button
                        onClick={() => setRoleFilter('secondary')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                            roleFilter === 'secondary'
                                ? 'bg-slate-900 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        🚀 Secondary ESPs (8)
                    </button>
                    <button
                        onClick={() => setRoleFilter('all')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                            roleFilter === 'all'
                                ? 'bg-slate-900 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        📊 All Systems incl. FeedBlitz (11)
                    </button>
                    <button
                        onClick={() => setRoleFilter('incubator')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                            roleFilter === 'incubator'
                                ? 'bg-slate-900 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        🌱 FeedBlitz Incubators (3)
                    </button>
                </div>
            </div>

            {/* Main 5-Pillar Multi-ESP Scorecard Table */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-gray-50/70">
                    <div>
                        <h3 className="text-sm font-bold text-gray-900">
                            Multi-ESP Scorecard Matrix (Click any row to inspect 5-Week Trajectory &amp; 3-Tier Funnel)
                        </h3>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                            <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block"></span> Tier 1 Hot (&le;14d)
                        </span>
                        <span className="flex items-center gap-1">
                            <span className="w-2.5 h-2.5 rounded-sm bg-blue-500 inline-block"></span> Tier 2 Warm (15-30d)
                        </span>
                        <span className="flex items-center gap-1">
                            <span className="w-2.5 h-2.5 rounded-sm bg-amber-400 inline-block"></span> Tier 3 Prove-It (0-7d)
                        </span>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                                <th className="py-3 px-4">ESP &amp; Brand</th>
                                <th className="py-3 px-3">Status &amp; Quota</th>
                                <th className="py-3 px-4">Pillar 1: Ready Segment</th>
                                <th className="py-3 px-3">7d Velocity &amp; Grad %</th>
                                <th className="py-3 px-3">Pillar 2: Reservoir Runway</th>
                                <th className="py-3 px-3">Pillar 3: 7d Engagement</th>
                                <th className="py-3 px-3">Human Clicks</th>
                                <th className="py-3 px-4">Pillar 4: 30d Revenue &amp; eCPM</th>
                                <th className="py-3 px-4">Pillar 5: Guardrails</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 text-xs">
                            {filteredEsps.map((row) => {
                                const isSelected = selectedEsp?.esp_id === row.esp_id;
                                const badge = STATUS_BADGE_STYLES[row.status_badge] || STATUS_BADGE_STYLES.STEADY;
                                const tierTotal = Math.max(
                                    1,
                                    (row.tier1_hot_14d || 0) + (row.tier2_warm_30d || 0) + (row.tier3_new_recruits || 0)
                                );
                                const t1Pct = Math.round(((row.tier1_hot_14d || 0) / tierTotal) * 100);
                                const t2Pct = Math.round(((row.tier2_warm_30d || 0) / tierTotal) * 100);
                                const t3Pct = Math.max(0, 100 - t1Pct - t2Pct);

                                const activeOpenerRate = row.open_rate_7d ?? 0;
                                const campaignOpenRate = row.campaign_open_rate_7d ?? 0;

                                return (
                                    <tr
                                        key={row.esp_id}
                                        onClick={() => setSelectedEspId(row.esp_id)}
                                        className={`cursor-pointer transition-colors ${
                                            isSelected
                                                ? 'bg-indigo-50/70 hover:bg-indigo-50'
                                                : 'hover:bg-gray-50'
                                        }`}
                                    >
                                        {/* ESP & Brand */}
                                        <td className="py-3.5 px-4">
                                            <div className="flex items-center gap-2.5">
                                                <img
                                                    src={`https://flagcdn.com/w20/${row.geo.toLowerCase()}.png`}
                                                    alt={row.geo}
                                                    className="w-4 h-3 rounded-sm object-cover flex-shrink-0"
                                                />
                                                <div>
                                                    <div className="font-bold text-gray-900 flex items-center gap-1.5">
                                                        {row.label}
                                                        <span className="text-[11px] font-mono text-gray-400">
                                                            ({row.esp_id})
                                                        </span>
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {row.brand} • <span className="font-mono">{row.sending_domain}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Status & Quota */}
                                        <td className="py-3.5 px-3">
                                            <span
                                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${badge.bg} ${badge.text}`}
                                            >
                                                {badge.label}
                                            </span>
                                            <div className="text-xs text-gray-500 mt-1">
                                                {row.role === 'secondary' ? (
                                                    <span>
                                                        Quota:{' '}
                                                        <strong className="text-gray-800">
                                                            {row.quota_range?.[0]}–{row.quota_range?.[1]}/d
                                                        </strong>
                                                    </span>
                                                ) : (
                                                    <span>Lead Incubator</span>
                                                )}
                                            </div>
                                        </td>

                                        {/* Pillar 1: Ready Segment & 3-Tier Bar */}
                                        <td className="py-3.5 px-4 min-w-[175px]">
                                            <div className="flex items-baseline justify-between">
                                                <span className="text-sm font-extrabold text-gray-900">
                                                    {row.ready_count.toLocaleString()}
                                                </span>
                                                <span className="text-[11px] text-gray-500">
                                                    Drift: {row.drift_pct.toFixed(1)}%
                                                </span>
                                            </div>
                                            {/* 3-Tier visual bar */}
                                            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden flex mt-1.5">
                                                <div
                                                    style={{ width: `${t1Pct}%` }}
                                                    className="bg-emerald-500 h-full"
                                                    title={`Tier 1 Hot (<=14d): ${row.tier1_hot_14d.toLocaleString()}`}
                                                />
                                                <div
                                                    style={{ width: `${t2Pct}%` }}
                                                    className="bg-blue-500 h-full"
                                                    title={`Tier 2 Warm (15-30d): ${row.tier2_warm_30d.toLocaleString()}`}
                                                />
                                                <div
                                                    style={{ width: `${t3Pct}%` }}
                                                    className="bg-amber-400 h-full"
                                                    title={`Tier 3 Fresh Recruits: ${row.tier3_new_recruits.toLocaleString()}`}
                                                />
                                            </div>
                                            <div className="flex justify-between text-[11px] text-gray-500 mt-1">
                                                <span>T1: {row.tier1_hot_14d.toLocaleString()}</span>
                                                <span>Pool: {row.active_total.toLocaleString()}</span>
                                            </div>
                                        </td>

                                        {/* 7d Inflow / Outflow & Recruit Graduation */}
                                        <td className="py-3.5 px-3">
                                            {row.role === 'secondary' ? (
                                                <div>
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="text-emerald-600 font-bold" title="7d Recruits Added">
                                                            +{row.recruits_7d.toLocaleString()} in
                                                        </span>
                                                        <span className="text-gray-300">|</span>
                                                        <span className="text-indigo-600 font-semibold" title="30d Recruit Graduation Rate">
                                                            {row.recruit_grad_pct_30d.toFixed(0)}% grad
                                                        </span>
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-0.5">
                                                        Pruned 7d: <span className="text-rose-600 font-medium">-{row.pruned_7d.toLocaleString()}</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="text-gray-400">Source Feed</span>
                                            )}
                                        </td>

                                        {/* Pillar 2: Reservoir & Runway */}
                                        <td className="py-3.5 px-3">
                                            {row.role === 'secondary' ? (
                                                <div>
                                                    <div className="font-bold text-gray-900">
                                                        {row.reservoir_total.toLocaleString()}{' '}
                                                        <span className="text-[11px] font-normal text-gray-500">proven</span>
                                                    </div>
                                                    <div className="text-xs mt-0.5">
                                                        {row.runway_days !== null ? (
                                                            <span
                                                                className={`font-semibold ${
                                                                    row.runway_days >= 25
                                                                        ? 'text-emerald-600'
                                                                        : row.runway_days >= 14
                                                                        ? 'text-amber-600'
                                                                        : 'text-rose-600'
                                                                }`}
                                                            >
                                                                ~{row.runway_days}d runway
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-400">Paused</span>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-xs text-purple-700 font-semibold">
                                                    Feeds {row.geo} Pool
                                                </div>
                                            )}
                                        </td>

                                        {/* Pillar 3: 7d Engagement */}
                                        <td className="py-3.5 px-3">
                                            <div className="flex items-center gap-1.5">
                                                <span
                                                    className={`text-sm font-extrabold ${
                                                        activeOpenerRate >= 75
                                                            ? 'text-emerald-600'
                                                            : activeOpenerRate >= 55
                                                            ? 'text-blue-600'
                                                            : 'text-amber-600'
                                                    }`}
                                                >
                                                    {activeOpenerRate.toFixed(1)}%
                                                </span>
                                                <span className="text-[11px] text-gray-400" title="7d Active Unique Openers % of Ready Segment">
                                                    7d Act
                                                </span>
                                            </div>
                                            <div className="text-xs text-gray-500 mt-0.5">
                                                {campaignOpenRate > 0
                                                    ? `Camp OR: ${campaignOpenRate.toFixed(1)}%`
                                                    : `Sent 7d: ${row.delivered_7d.toLocaleString()}`}
                                            </div>
                                        </td>

                                        {/* Human Affiliate Clicks */}
                                        <td className="py-3.5 px-3">
                                            <div className="font-bold text-gray-900">
                                                {row.aff_clicks_7d.toLocaleString()}{' '}
                                                <span className="text-[11px] font-normal text-gray-500">(7d)</span>
                                            </div>
                                            <div className="text-xs text-gray-500 mt-0.5">
                                                30d: <strong className="text-gray-700">{row.aff_clicks_30d.toLocaleString()}</strong>
                                            </div>
                                        </td>

                                        {/* Pillar 4: 30d Monetization */}
                                        <td className="py-3.5 px-4">
                                            <div className="font-extrabold text-gray-900 text-sm">
                                                {Math.round(row.rev_dkk_30d).toLocaleString()} DKK
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                                                <span>{row.conv_30d} conv</span>
                                                <span>•</span>
                                                <span className="font-semibold text-indigo-600">
                                                    {row.ecpm_30d.toFixed(1)} eCPM
                                                </span>
                                            </div>
                                        </td>

                                        {/* Pillar 5: Deliverability Guardrails */}
                                        <td className="py-3.5 px-4">
                                            <div className="flex items-center gap-1.5">
                                                <span
                                                    className={`px-2 py-0.5 rounded text-[10px] font-bold border ${
                                                        POSTMASTER_BADGE[row.domain_reputation] || POSTMASTER_BADGE.NONE
                                                    }`}
                                                >
                                                    {row.domain_reputation}
                                                </span>
                                                <span
                                                    className={`text-xs font-bold ${
                                                        row.spam_rate_pct_7d >= 0.3
                                                            ? 'text-rose-600'
                                                            : 'text-gray-700'
                                                    }`}
                                                >
                                                    {row.spam_rate_pct_7d.toFixed(2)}% spam
                                                </span>
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">
                                                Bounce: {row.bounce_rate_30d.toFixed(2)}% • Unsub: {row.unsub_rate_30d.toFixed(2)}%
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Selected ESP Drill-Down Panel */}
            {selectedEsp && (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Left Column: 3-Tier SendSegment & Reservoir Anatomy */}
                    <div className="lg:col-span-5 bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex flex-col justify-between">
                        <div>
                            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                                <div className="flex items-center gap-2">
                                    <img
                                        src={`https://flagcdn.com/w20/${selectedEsp.geo.toLowerCase()}.png`}
                                        alt={selectedEsp.geo}
                                        className="w-5 h-3.5 rounded-sm object-cover"
                                    />
                                    <div>
                                        <h4 className="text-sm font-bold text-gray-900">
                                            {selectedEsp.label} ({selectedEsp.esp_id}) — Funnel &amp; Tier Anatomy
                                        </h4>
                                        <p className="text-xs text-gray-500">
                                            {selectedEsp.brand} • {selectedEsp.sending_domain}
                                        </p>
                                    </div>
                                </div>
                                {onSelectEspForDeepDive && selectedEsp.role === 'secondary' && (
                                    <button
                                        onClick={() => onSelectEspForDeepDive(selectedEsp.geo, selectedEsp.esp_id)}
                                        className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold transition-colors"
                                    >
                                        Single-ESP Deep Dive →
                                    </button>
                                )}
                            </div>

                            {/* Tier Breakdown Rows */}
                            <div className="space-y-3 mt-4 text-xs">
                                <div className="p-3 rounded-lg bg-emerald-50/70 border border-emerald-100 flex items-center justify-between">
                                    <div>
                                        <div className="font-bold text-emerald-900">
                                            Tier 1: Core Hot Openers (&le;14d Active)
                                        </div>
                                        <div className="text-emerald-700">
                                            Sent daily • Anchors Postmaster reputation
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm font-extrabold text-emerald-900">
                                            {selectedEsp.tier1_hot_14d.toLocaleString()}
                                        </div>
                                        <div className="text-emerald-700">
                                            {selectedEsp.ready_count > 0
                                                ? `${Math.round((selectedEsp.tier1_hot_14d / selectedEsp.ready_count) * 100)}% of Ready`
                                                : '0%'}
                                        </div>
                                    </div>
                                </div>

                                <div className="p-3 rounded-lg bg-blue-50/70 border border-blue-100 flex items-center justify-between">
                                    <div>
                                        <div className="font-bold text-blue-900">
                                            Tier 2: Rotating Warm Openers (15–30d)
                                        </div>
                                        <div className="text-blue-700">
                                            25% deterministic daily hash slice
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm font-extrabold text-blue-900">
                                            {selectedEsp.tier2_warm_30d.toLocaleString()}
                                        </div>
                                        <div className="text-blue-700">Active 15-30d pool</div>
                                    </div>
                                </div>

                                <div className="p-3 rounded-lg bg-amber-50/70 border border-amber-100 flex items-center justify-between">
                                    <div>
                                        <div className="font-bold text-amber-900">
                                            Tier 3: Fresh Recruits Prove-It Cadence
                                        </div>
                                        <div className="text-amber-700">
                                            4-touch cadence • Quota: {selectedEsp.quota_range?.[0]}–{selectedEsp.quota_range?.[1]}/day ({selectedEsp.recruit_grad_pct_30d}% grad)
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm font-extrabold text-amber-900">
                                            {selectedEsp.tier3_new_recruits.toLocaleString()}
                                        </div>
                                        <div className="text-amber-700">
                                            +{selectedEsp.recruits_7d.toLocaleString()} added (7d)
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-2 pt-2">
                                    <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200">
                                        <div className="text-gray-500">Resting Pool</div>
                                        <div className="text-sm font-bold text-gray-800 mt-0.5">
                                            {selectedEsp.inactive_count.toLocaleString()}
                                        </div>
                                    </div>
                                    <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200">
                                        <div className="text-gray-500">Stage 1+2 Reservoir</div>
                                        <div className="text-sm font-bold text-indigo-700 mt-0.5">
                                            {selectedEsp.reservoir_total.toLocaleString()}
                                        </div>
                                    </div>
                                    <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200">
                                        <div className="text-gray-500">Pruned (30d)</div>
                                        <div className="text-sm font-bold text-rose-700 mt-0.5">
                                            {selectedEsp.pruned_30d.toLocaleString()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                            <span>Shielded Converters: <strong>{selectedEsp.shielded_converters.toLocaleString()}</strong></span>
                            <span>EPC (30d): <strong>{selectedEsp.epc_30d.toFixed(2)} DKK / click</strong></span>
                        </div>
                    </div>

                    {/* Right Column: 5-Week Trajectory Chart */}
                    <div className="lg:col-span-7 bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-100 pb-3 mb-4">
                            <div>
                                <h4 className="text-sm font-bold text-gray-900">
                                    📈 {selectedEsp.label} — 5-Week Performance &amp; Monetization Trajectory
                                </h4>
                                <p className="text-xs text-gray-500">
                                    Weekly progression of revenue, conversions, delivered volume, and unique opens
                                </p>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <button
                                    onClick={() => setChartMode('revenue')}
                                    className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                                        chartMode === 'revenue'
                                            ? 'bg-indigo-600 text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    💰 Revenue &amp; Conversions
                                </button>
                                <button
                                    onClick={() => setChartMode('engagement')}
                                    className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                                        chartMode === 'engagement'
                                            ? 'bg-indigo-600 text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    📬 Delivered &amp; Opens
                                </button>
                            </div>
                        </div>

                        <div className="h-72 w-full">
                            {selectedEsp.weekly_trends && selectedEsp.weekly_trends.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    {chartMode === 'revenue' ? (
                                        <ComposedChart data={selectedEsp.weekly_trends}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                                            <YAxis
                                                yAxisId="left"
                                                tick={{ fontSize: 11 }}
                                                label={{ value: 'Revenue (DKK)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
                                            />
                                            <YAxis
                                                yAxisId="right"
                                                orientation="right"
                                                tick={{ fontSize: 11 }}
                                                label={{ value: 'Conversions', angle: 90, position: 'insideRight', style: { fontSize: 11 } }}
                                            />
                                            <Tooltip />
                                            <Legend wrapperStyle={{ fontSize: 12 }} />
                                            <Bar
                                                yAxisId="left"
                                                dataKey="revenue_dkk"
                                                name="Revenue (DKK)"
                                                fill="#4f46e5"
                                                radius={[4, 4, 0, 0]}
                                            />
                                            <Line
                                                yAxisId="right"
                                                type="monotone"
                                                dataKey="conversions"
                                                name="Conversions"
                                                stroke="#10b981"
                                                strokeWidth={2.5}
                                                dot={{ r: 4 }}
                                            />
                                        </ComposedChart>
                                    ) : (
                                        <ComposedChart data={selectedEsp.weekly_trends}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                                            <YAxis
                                                yAxisId="left"
                                                tick={{ fontSize: 11 }}
                                                label={{ value: 'Delivered', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
                                            />
                                            <YAxis
                                                yAxisId="right"
                                                orientation="right"
                                                tick={{ fontSize: 11 }}
                                                label={{ value: 'Unique Opens', angle: 90, position: 'insideRight', style: { fontSize: 11 } }}
                                            />
                                            <Tooltip />
                                            <Legend wrapperStyle={{ fontSize: 12 }} />
                                            <Bar
                                                yAxisId="left"
                                                dataKey="delivered"
                                                name="Delivered Volume"
                                                fill="#0ea5e9"
                                                radius={[4, 4, 0, 0]}
                                            />
                                            <Line
                                                yAxisId="right"
                                                type="monotone"
                                                dataKey="opens"
                                                name="Unique Opens"
                                                stroke="#f59e0b"
                                                strokeWidth={2.5}
                                                dot={{ r: 4 }}
                                            />
                                        </ComposedChart>
                                    )}
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex items-center justify-center text-xs text-gray-400">
                                    No weekly history available for this ESP.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EspKpiCommandCenter;
